import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, Input, PageHeader, Pagination, Select, Table } from '../../design';
import { fmtRelative } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useOrgOptions } from '../users/UsersPage';
import {
  type Locker,
  type LockerStatus,
  type LockerType,
  STATUS,
  StatusBadge,
  TYPE_LABEL,
} from './common';
import { LockerWizard } from './LockerWizard';
import { ExportButton } from '../reports/ExportButton';

export function LockersPage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const { values, page, pageSize, set } = useUrlState(['q', 'type', 'status', 'orgId'] as const);
  const [wizard, setWizard] = useState(false);
  const orgs = useOrgOptions();
  const orgName = (id: string) => orgs.data?.items.find((o) => o.id === id)?.name ?? '…';
  const query = useQuery({
    queryKey: ['lockers', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/lockers', {
          params: {
            query: queryParams({
              q: values.q,
              type: (values.type || undefined) as LockerType | undefined,
              status: (values.status || undefined) as LockerStatus | undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Lockere"
        subtitle="Echipamentele din perimetrul tău: stare, versiunea configurației și controlerul."
        actions={
          <span className="flex">
            <ExportButton
              kind="lockers"
              params={{
                q: values.q || undefined,
                type: values.type || undefined,
                status: values.status || undefined,
                orgId: values.orgId || undefined,
              }}
            />
            {has('configure') && (
              <Button variant="primary" icon="plus" onClick={() => setWizard(true)}>
                Locker nou
              </Button>
            )}
          </span>
        }
      />
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Input
            placeholder="Caută după nume, serie sau oraș…"
            value={values.q}
            onChange={(e) => set({ q: e.target.value })}
            style={{ maxWidth: 300 }}
            aria-label="Căutare"
          />
          <Select
            value={values.type}
            onChange={(e) => set({ type: e.target.value })}
            style={{ maxWidth: 200 }}
            aria-label="Tip"
          >
            <option value="">Toate tipurile</option>
            {(Object.keys(TYPE_LABEL) as LockerType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 180 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            {(Object.keys(STATUS) as LockerStatus[]).map((st) => (
              <option key={st} value={st}>
                {STATUS[st].label}
              </option>
            ))}
          </Select>
          {user?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Table<Locker>
          columns={[
            {
              key: 'name',
              header: 'Locker',
              render: (l) => (
                <div>
                  <strong>{l.name}</strong>
                  <div className="small muted mono">{l.serialNumber}</div>
                </div>
              ),
            },
            { key: 'type', header: 'Tip', render: (l) => TYPE_LABEL[l.type] },
            ...(user?.orgId === null
              ? [{ key: 'org', header: 'Organizație', render: (l: Locker) => orgName(l.orgId) }]
              : []),
            { key: 'city', header: 'Oraș', render: (l) => l.location.city },
            { key: 'status', header: 'Stare', render: (l) => <StatusBadge status={l.status} /> },
            {
              key: 'version',
              header: 'Configurație',
              render: (l) => (
                <span
                  className={l.configVersion !== l.deployedVersion ? 'strong' : ''}
                  style={
                    l.configVersion !== l.deployedVersion ? { color: 'var(--orange)' } : undefined
                  }
                >
                  v{l.configVersion}{' '}
                  {l.configVersion !== l.deployedVersion ? `(instalată v${l.deployedVersion})` : ''}
                </span>
              ),
            },
            {
              key: 'seen',
              header: 'Ultimul contact',
              render: (l) => <span className="muted">{fmtRelative(l.lastSeenAt)}</span>,
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(l) => l.id}
          onRowClick={(l) => navigate(`/lockere/${l.id}`)}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Niciun locker.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {wizard && (
        <LockerWizard
          onClose={() => setWizard(false)}
          onCreated={(id) => navigate(`/lockere/${id}`)}
        />
      )}
    </>
  );
}
